import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

/**
 * Create or update a student profile
 */
export const createStudentProfile = async (userId, profileData) => {
  try {
    await setDoc(doc(db, "studentProfiles", userId), {
      ...profileData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(doc(db, "users", userId), {
      profileCompleted: true,
    });

    return true;
  } catch (error) {
    throw new Error("Failed to create student profile: " + error.message);
  }
};

/**
 * Create or update a company profile
 */
export const createCompanyProfile = async (userId, profileData) => {
  try {
    await setDoc(doc(db, "companyProfiles", userId), {
      ...profileData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(doc(db, "users", userId), {
      profileCompleted: true,
    });

    return true;
  } catch (error) {
    throw new Error("Failed to create company profile: " + error.message);
  }
};

/**
 * Get a student profile
 */
export const getStudentProfile = async (userId) => {
  try {
    const profileDoc = await getDoc(doc(db, "studentProfiles", userId));

    if (!profileDoc.exists()) {
      return null;
    }

    return profileDoc.data();
  } catch (error) {
    console.error("Error fetching student profile:", error);
    return null;
  }
};

/**
 * Get a company profile
 */
export const getCompanyProfile = async (userId) => {
  try {
    const profileDoc = await getDoc(doc(db, "companyProfiles", userId));

    if (!profileDoc.exists()) {
      return null;
    }

    return profileDoc.data();
  } catch (error) {
    console.error("Error fetching company profile:", error);
    return null;
  }
};

/**
 * Upload profile image
 */
export const uploadProfileImage = async (userId, imageFile, userType) => {
  try {
    console.log('Profile service: Starting upload for', { userId, userType, fileName: imageFile.name, fileSize: imageFile.size });
    
    // Validate file
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('Please select a valid image file');
    }
    
    if (imageFile.size > 2 * 1024 * 1024) {
      throw new Error('File size must be less than 2MB');
    }

    const storageRef = ref(storage, `profileImages/${userType}/${userId}`);
    console.log('Profile service: Uploading to path:', `profileImages/${userType}/${userId}`);
    
    const uploadResult = await uploadBytes(storageRef, imageFile);
    console.log('Profile service: Upload successful', uploadResult);

    const downloadURL = await getDownloadURL(storageRef);
    console.log('Profile service: Got download URL', downloadURL);

    const profileRef = doc(db, `${userType}Profiles`, userId);
    await updateDoc(profileRef, {
      photoURL: downloadURL,
      updatedAt: serverTimestamp(),
    });
    console.log('Profile service: Updated Firestore document');

    return downloadURL;
  } catch (error) {
    console.error('Profile service: Upload failed', error);
    throw new Error("Failed to upload profile image: " + error.message);
  }
};

/**
 * Update student profile
 */
export const updateStudentProfile = async (userId, updateData) => {
  try {
    const profileRef = doc(db, "studentProfiles", userId);

    await updateDoc(profileRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    throw new Error("Failed to update student profile: " + error.message);
  }
};

/**
 * Update company profile
 */
export const updateCompanyProfile = async (userId, updateData) => {
  try {
    const profileRef = doc(db, "companyProfiles", userId);

    await updateDoc(profileRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    throw new Error("Failed to update company profile: " + error.message);
  }
};